import {
  type Asset,
  type AssetCutout,
  type AssetMatte,
  type AssetMedia,
  addPage,
  applyDeviceScreenshot,
  arrangeBoard,
  bindColor,
  bindTextStyle,
  boundingRect,
  brandScale,
  buildParentMap,
  type ClipboardData,
  copyElements,
  createIconElement,
  createImageElement,
  createQrElement,
  createShapeElement,
  createSvgElement,
  createTextElement,
  duplicateElements,
  duplicatePage,
  fitSize,
  groupElements,
  ICON_WEIGHTS,
  type IconWeight,
  imageFromPlaceholder,
  insertElements,
  isImagePlaceholder,
  loadIconPaths,
  materializeBoard,
  movePage,
  nextEmptyCell,
  type PlacedElements,
  pageSize,
  pasteElements,
  type Rect,
  type ReorderMode,
  removeElements,
  removePage,
  reorderElements,
  reorderVideoElements,
  type SceneDocument,
  type SceneElement,
  type ShapeKind,
  setWorldBox,
  supportsBoard,
  type TextStyle,
  ungroupElement,
  unionRects,
  worldBox,
} from "@cva/shared";
import { activePageDoc, type EditorStore } from "./store";

/** Editor commands on top of the shared document operations. */

export function selectionRect(doc: SceneDocument, ids: string[]): Rect | null {
  const parents = buildParentMap(doc);
  return unionRects(
    ids.flatMap((id) => {
      const box = worldBox(doc, id, parents);
      return box ? [boundingRect(box)] : [];
    }),
  );
}

function centerOnPage(doc: SceneDocument, width: number, height: number) {
  return { x: (doc.format.width - width) / 2, y: (doc.format.height - height) / 2 };
}

export function addElements(store: EditorStore, elements: SceneElement[]) {
  const { pageIndex } = store.getState();
  store.getState().update((draft) => insertElements(draft, pageIndex, elements));
  store.getState().select(elements.map((element) => element.id));
}

/**
 * Inserts a building block (built-in or saved): its groups' children go into
 * the element map, the top-level elements onto the page, selected.
 */
export function addPlacedElements(store: EditorStore, placed: PlacedElements) {
  if (placed.roots.length === 0) return;
  const roots = new Set(placed.roots.map((root) => root.id));
  const { pageIndex } = store.getState();
  store.getState().update((draft) => {
    for (const element of placed.elements) {
      if (!roots.has(element.id)) draft.elements[element.id] = element;
    }
    insertElements(draft, pageIndex, placed.roots);
  });
  store.getState().select([...roots]);
}

export type TextPreset = "heading" | "subheading" | "body";

/** Brand text style used for the quick text presets. */
const PRESET_TEXT_STYLE: Record<TextPreset, string> = {
  heading: "heading-1",
  subheading: "subline",
  body: "body",
};

/** Adds a text in a brand text style (linked to the kit). */
export function addBrandText(
  store: EditorStore,
  styleKey: string,
  text: string,
  at?: { x: number; y: number },
) {
  const { brand } = store.getState();
  const doc = activePageDoc(store);
  if (!brand) return null;
  const scale = brandScale(doc);
  const element = createTextElement({ text });
  if (!bindTextStyle(element, styleKey, brand, scale)) return null;
  element.width = Math.round(doc.format.width * 0.7);
  element.height = Math.ceil(element.style.fontSize * element.style.lineHeight);
  const position = at
    ? { x: at.x - element.width / 2, y: at.y - element.height / 2 }
    : centerOnPage(doc, element.width, element.height);
  Object.assign(element, position);
  addElements(store, [element]);
  store.getState().setEditingText(element.id);
  return element.id;
}

export function addText(store: EditorStore, preset: TextPreset, at?: { x: number; y: number }) {
  const { brand } = store.getState();
  const doc = activePageDoc(store);
  if (brand) {
    const label = {
      heading: "Überschrift",
      subheading: "Unterüberschrift",
      body: "Dein Text hier",
    };
    const id = addBrandText(store, PRESET_TEXT_STYLE[preset], label[preset], at);
    if (id) return id;
  }
  const base = Math.min(doc.format.width, doc.format.height * 1.2);
  const styles: Record<TextPreset, Partial<TextStyle> & { text: string }> = {
    heading: {
      text: "Überschrift",
      fontSize: Math.round(base * 0.085),
      fontWeight: 800,
      lineHeight: 1.1,
    },
    subheading: {
      text: "Unterüberschrift",
      fontSize: Math.round(base * 0.05),
      fontWeight: 600,
      lineHeight: 1.2,
    },
    body: {
      text: "Dein Text hier",
      fontSize: Math.round(base * 0.032),
      fontWeight: 400,
      lineHeight: 1.4,
    },
  };
  const { text, ...style } = styles[preset];
  const width = Math.round(doc.format.width * 0.7);
  const height = Math.ceil((style.fontSize ?? 40) * (style.lineHeight ?? 1.2));
  const position = at
    ? { x: at.x - width / 2, y: at.y - height / 2 }
    : centerOnPage(doc, width, height);
  const element = createTextElement({ text, x: position.x, y: position.y, width, height, style });
  addElements(store, [element]);
  store.getState().setEditingText(element.id);
  return element.id;
}

export function addShape(store: EditorStore, shape: ShapeKind, variant?: "rounded" | "dashed") {
  const doc = activePageDoc(store);
  const size = Math.round(Math.min(doc.format.width, doc.format.height) * 0.3);
  const line = shape === "line";
  const width = line ? Math.round(doc.format.width * 0.5) : size;
  const height = line ? Math.max(4, Math.round(size * 0.04)) : size;
  const strokeWidth = Math.max(2, Math.round(size * 0.02));
  const { brand } = store.getState();
  const element = createShapeElement(shape, {
    ...centerOnPage(doc, width, height),
    width,
    height,
    fill: "#2457eb",
    radius: variant === "rounded" ? Math.round(size * 0.12) : 0,
    ...(line
      ? {
          height: Math.max(strokeWidth * 3, 12),
          stroke: {
            color: "#111111",
            width: strokeWidth,
            dash: variant === "dashed" ? "dashed" : "solid",
          },
        }
      : {}),
  });
  // New shapes start in the brand colours.
  if (brand) {
    if (line) bindColor(element, "stroke", "text", brand);
    else bindColor(element, "fill", "primary", brand);
  }
  addElements(store, [element]);
}

/** Adds a QR code: square, centred, big enough to scan (print: at least 25 mm). */
export function addQr(store: EditorStore, value: string) {
  const doc = activePageDoc(store);
  const short = Math.min(doc.format.width, doc.format.height);
  const print = doc.format.print;
  const minimum = print ? (25 * doc.format.width) / print.widthMm : 0;
  const size = Math.round(Math.min(short * 0.6, Math.max(short * 0.28, minimum)));
  const element = createQrElement(value, {
    ...centerOnPage(doc, size, size),
    width: size,
    height: size,
    // Pure black prints on the black plate only (sharpest edges).
    color: "#000000",
  });
  addElements(store, [element]);
  return element.id;
}

/** Drag & drop of library icons onto the canvas: JSON `{ name, weight }`. */
export const ICON_DRAG_TYPE = "application/x-cva-icon";

export function parseIconDrag(data: string): { name: string; weight: IconWeight } | null {
  try {
    const value = JSON.parse(data) as { name?: unknown; weight?: unknown };
    const weight = ICON_WEIGHTS.find((entry) => entry === value.weight);
    if (typeof value.name !== "string" || !/^[a-z0-9-]{1,64}$/.test(value.name) || !weight)
      return null;
    return { name: value.name, weight };
  } catch {
    return null;
  }
}

/**
 * Adds a library icon (centred or at `at`), a fifth of the shorter page side.
 * Its colour follows the brand's text colour (or primary colour) when a kit is
 * linked, otherwise a dark neutral grey (prints on the black plate only).
 */
export async function addIcon(
  store: EditorStore,
  name: string,
  weight: IconWeight,
  at?: { x: number; y: number },
): Promise<string | null> {
  const paths = await loadIconPaths(name, weight);
  if (!paths || store.getState().readOnly) return null;
  const { brand } = store.getState();
  const doc = activePageDoc(store);
  const size = Math.round(Math.min(doc.format.width, doc.format.height) * 0.2);
  const position = at ? { x: at.x - size / 2, y: at.y - size / 2 } : centerOnPage(doc, size, size);
  const element = createIconElement(name, weight, paths, {
    ...position,
    width: size,
    height: size,
    color: "#1a1a1a",
  });
  if (brand && !bindColor(element, "color", "text", brand)) {
    bindColor(element, "color", "primary", brand);
  }
  addElements(store, [element]);
  return element.id;
}

/** Switches the style of icons: the paths are read from the catalog again (one undo step). */
export async function setIconWeight(store: EditorStore, ids: string[], weight: IconWeight) {
  const { doc } = store.getState();
  const names = [
    ...new Set(
      ids.flatMap((id) => {
        const element = doc.elements[id];
        return element?.type === "icon" ? [element.icon] : [];
      }),
    ),
  ];
  const paths = new Map(
    await Promise.all(
      names.map(async (name) => [name, await loadIconPaths(name, weight)] as const),
    ),
  );
  store.getState().update((draft) => {
    for (const id of ids) {
      const element = draft.elements[id];
      const next = element?.type === "icon" ? paths.get(element.icon) : null;
      if (element?.type !== "icon" || !next || element.locked) continue;
      element.weight = weight;
      element.paths = next;
    }
  });
}

/** "Icon tauschen": another icon in the same box, style and colour. */
export async function replaceIcon(store: EditorStore, id: string, name: string) {
  const element = store.getState().doc.elements[id];
  if (element?.type !== "icon") return;
  const paths = await loadIconPaths(name, element.weight);
  if (!paths) return;
  store.getState().update((draft) => {
    const target = draft.elements[id];
    if (target?.type !== "icon") return;
    target.icon = name;
    target.paths = paths;
  });
}

export interface AssetInfo {
  id: string;
  kind: string;
  url: string;
  thumbnailUrl?: string | null;
  width: number | null;
  height: number | null;
  name: string;
  /** Videos and audio. */
  durationMs?: number | null;
  /** Videos and audio: preview, filmstrip, waveform. */
  media?: AssetMedia | null;
  /** Cut-out copy of another image (background removed). */
  cutout?: AssetCutout | null;
  /** Videos: the cut-out person ("Person freistellen"). */
  matte?: AssetMatte | null;
  /** Upload or background removal still running, or failed. */
  status?: Asset["status"];
}

export function addAsset(
  store: EditorStore,
  asset: AssetInfo,
  at?: { x: number; y: number },
): string {
  const { selection } = store.getState();
  const doc = activePageDoc(store);
  // A selected collage (group with empty cells) is filled cell by cell.
  if (!at && asset.kind === "image" && selection.length === 1) {
    const cell = nextEmptyCell(doc, selection[0]!);
    if (cell) {
      replaceImage(store, cell, asset.id, asset);
      return cell;
    }
  }
  const natural = { width: asset.width ?? 800, height: asset.height ?? 600 };
  const size = fitSize(natural, { width: doc.format.width * 0.6, height: doc.format.height * 0.6 });
  const position = at
    ? { x: at.x - size.width / 2, y: at.y - size.height / 2 }
    : centerOnPage(doc, size.width, size.height);
  const element =
    asset.kind === "svg"
      ? createSvgElement(asset.id, { ...position, ...size, name: asset.name })
      : createImageElement(asset.id, { ...position, ...size, name: asset.name });
  addElements(store, [element]);
  return element.id;
}

/**
 * Replaces the image of an image element and resets its crop. An image
 * placeholder (templates) becomes an image in its place and shape; a device
 * mockup shows the picture on its screen (`size`: the picture's pixels, so the
 * device turns to its orientation).
 */
export function replaceImage(
  store: EditorStore,
  elementId: string,
  assetId: string,
  size?: { width: number | null; height: number | null } | null,
  previous?: { width: number | null; height: number | null } | null,
) {
  store.getState().update((draft) => {
    const element = draft.elements[elementId];
    if (element?.type === "device") {
      applyDeviceScreenshot(element, assetId, size, previous);
    } else if (element?.type === "image" || element?.type === "svg") {
      element.assetId = assetId;
      if (element.type === "image") element.crop = null;
    } else if (isImagePlaceholder(element)) {
      draft.elements[elementId] = imageFromPlaceholder(element, assetId);
    }
  });
}

/** The screenshot of a device mockup (to know the size of the picture it replaces). */
export function screenshotOf(element: SceneElement | undefined): string | null {
  return element?.type === "device" ? (element.assetId ?? null) : null;
}

/** Elements an image can be dropped into: images, image placeholders and device mockups. */
export function acceptsImage(element: SceneElement | undefined): boolean {
  return element?.type === "image" || element?.type === "device" || isImagePlaceholder(element);
}

export function deleteSelection(store: EditorStore) {
  const { selection, doc } = store.getState();
  const deletable = selection.filter((id) => !doc.elements[id]?.locked);
  if (deletable.length === 0) return;
  store.getState().update((draft) => removeElements(draft, deletable));
  store.getState().select([]);
}

export function duplicateSelection(store: EditorStore) {
  const { selection } = store.getState();
  if (selection.length === 0) return;
  let created: string[] = [];
  store.getState().update((draft) => {
    created = duplicateElements(draft, selection);
  });
  store.getState().select(created);
}

export function groupSelection(store: EditorStore) {
  const { selection } = store.getState();
  let groupId: string | null = null;
  store.getState().update((draft) => {
    groupId = groupElements(draft, selection);
  });
  if (groupId) store.getState().select([groupId]);
}

export function ungroupSelection(store: EditorStore) {
  const { selection, doc } = store.getState();
  const groups = selection.filter((id) => doc.elements[id]?.type === "group");
  if (groups.length === 0) return;
  const children: string[] = [];
  store.getState().update((draft) => {
    for (const id of groups) children.push(...ungroupElement(draft, id));
  });
  store.getState().select(children);
}

export function reorderSelection(store: EditorStore, mode: ReorderMode) {
  const { selection, doc } = store.getState();
  if (selection.length === 0) return;
  store.getState().update((draft) => {
    if (doc.kind !== "video") return reorderElements(draft, selection, mode);
    // Videos: top-level elements move along the tracks, the content of groups as usual.
    const top = new Set(draft.pages[0]?.children ?? []);
    const onTracks = selection.filter((id) => top.has(id));
    if (onTracks.length > 0) reorderVideoElements(draft, onTracks, mode);
    const inGroups = selection.filter((id) => !top.has(id));
    if (inGroups.length > 0) reorderElements(draft, inGroups, mode);
  });
}

export function nudgeSelection(store: EditorStore, dx: number, dy: number) {
  const { selection, doc } = store.getState();
  const movable = selection.filter((id) => !doc.elements[id]?.locked);
  if (movable.length === 0) return;
  store.getState().update(
    (draft) => {
      const parents = buildParentMap(draft);
      for (const id of movable) {
        const box = worldBox(draft, id, parents);
        if (box) setWorldBox(draft, id, { ...box, x: box.x + dx, y: box.y + dy });
      }
    },
    { merge: "nudge" },
  );
}

export function toggleLock(store: EditorStore) {
  const { selection, doc } = store.getState();
  if (selection.length === 0) return;
  const lock = !selection.every((id) => doc.elements[id]?.locked);
  store.getState().update((draft) => {
    for (const id of selection) {
      const element = draft.elements[id];
      if (element) element.locked = lock;
    }
  });
}

export function toggleHidden(store: EditorStore, ids?: string[]) {
  const { selection, doc } = store.getState();
  const targets = ids ?? selection;
  if (targets.length === 0) return;
  const hide = !targets.every((id) => doc.elements[id]?.hidden);
  store.getState().update((draft) => {
    for (const id of targets) {
      const element = draft.elements[id];
      if (element) element.hidden = hide;
    }
  });
}

export type AlignMode = "left" | "center" | "right" | "top" | "middle" | "bottom";

/** Aligns to the page (single element) or to the selection bounds. */
export function alignSelection(store: EditorStore, mode: AlignMode) {
  const { selection } = store.getState();
  const doc = activePageDoc(store);
  const ids = selection.filter((id) => !doc.elements[id]?.locked);
  if (ids.length === 0) return;
  const target =
    ids.length === 1
      ? { x: 0, y: 0, width: doc.format.width, height: doc.format.height }
      : selectionRect(doc, ids);
  if (!target) return;
  store.getState().update((draft) => {
    const parents = buildParentMap(draft);
    for (const id of ids) {
      const box = worldBox(draft, id, parents);
      if (!box) continue;
      const bounds = boundingRect(box);
      let dx = 0;
      let dy = 0;
      if (mode === "left") dx = target.x - bounds.x;
      if (mode === "center") dx = target.x + target.width / 2 - (bounds.x + bounds.width / 2);
      if (mode === "right") dx = target.x + target.width - (bounds.x + bounds.width);
      if (mode === "top") dy = target.y - bounds.y;
      if (mode === "middle") dy = target.y + target.height / 2 - (bounds.y + bounds.height / 2);
      if (mode === "bottom") dy = target.y + target.height - (bounds.y + bounds.height);
      setWorldBox(draft, id, { ...box, x: box.x + dx, y: box.y + dy });
    }
  });
}

/** Even spacing between three or more elements. */
export function distributeSelection(store: EditorStore, axis: "x" | "y") {
  const { selection, doc } = store.getState();
  const ids = selection.filter((id) => !doc.elements[id]?.locked);
  if (ids.length < 3) return;
  store.getState().update((draft) => {
    const parents = buildParentMap(draft);
    const items = ids
      .map((id) => ({ id, box: worldBox(draft, id, parents)! }))
      .filter((item) => item.box)
      .map((item) => ({ ...item, rect: boundingRect(item.box) }))
      .sort((a, b) => (axis === "x" ? a.rect.x - b.rect.x : a.rect.y - b.rect.y));
    const first = items[0]!.rect;
    const last = items[items.length - 1]!.rect;
    const size = (rect: Rect) => (axis === "x" ? rect.width : rect.height);
    const total = items.reduce((sum, item) => sum + size(item.rect), 0);
    const span = axis === "x" ? last.x + last.width - first.x : last.y + last.height - first.y;
    const gap = (span - total) / (items.length - 1);
    let cursor = axis === "x" ? first.x : first.y;
    for (const item of items) {
      const delta = cursor - (axis === "x" ? item.rect.x : item.rect.y);
      setWorldBox(draft, item.id, {
        ...item.box,
        x: item.box.x + (axis === "x" ? delta : 0),
        y: item.box.y + (axis === "y" ? delta : 0),
      });
      cursor += size(item.rect) + gap;
    }
  });
}

// --- Clipboard ------------------------------------------------------------------

const CLIPBOARD_MARKER = "cva-elements/v1:";
let internalClipboard: ClipboardData | null = null;
let pasteCount = 0;

export function copySelection(store: EditorStore): string | null {
  const { selection, doc } = store.getState();
  if (selection.length === 0) return null;
  internalClipboard = copyElements(doc, selection);
  pasteCount = 0;
  return `${CLIPBOARD_MARKER}${JSON.stringify(internalClipboard)}`;
}

export function parseClipboard(text: string | null | undefined): ClipboardData | null {
  if (!text?.startsWith(CLIPBOARD_MARKER)) return null;
  try {
    const data = JSON.parse(text.slice(CLIPBOARD_MARKER.length)) as ClipboardData;
    return data.roots && data.elements ? data : null;
  } catch {
    return null;
  }
}

export function pasteData(store: EditorStore, data: ClipboardData | null = internalClipboard) {
  if (!data) return;
  pasteCount += 1;
  const offset = 20 * pasteCount;
  let created: string[] = [];
  const { pageIndex } = store.getState();
  store.getState().update((draft) => {
    created = pasteElements(draft, pageIndex, data, { x: offset, y: offset });
  });
  store.getState().select(created);
}

// --- Pages ----------------------------------------------------------------------

/** New page after the active one; without a size it has the size of the active page. */
export function addPageAfter(store: EditorStore, size?: { width: number; height: number }) {
  const { pageIndex, doc } = store.getState();
  const background = doc.pages[pageIndex]?.background ?? "#ffffff";
  const own = size ?? pageSize(doc, doc.pages[pageIndex]);
  store.getState().update((draft) => {
    addPage(draft, pageIndex + 1, typeof background === "string" ? background : "#ffffff", own);
  });
  store.getState().setPage(pageIndex + 1);
  focusActivePage(store);
}

function requestFit(store: EditorStore, mode: "all" | "page") {
  store.getState().requestFit(mode);
}

function focusActivePage(store: EditorStore) {
  store.getState().requestFit("focus");
}

/** New page behind the last one (the "+" on the board), the size of the active page. */
export function addPageAfterLast(store: EditorStore) {
  const { doc, pageIndex } = store.getState();
  const own = pageSize(doc, doc.pages[pageIndex]);
  const background = doc.pages[pageIndex]?.background ?? "#ffffff";
  const last = doc.pages.length;
  store.getState().update((draft) => {
    addPage(draft, last, typeof background === "string" ? background : "#ffffff", own);
  });
  store.getState().setPage(last);
  focusActivePage(store);
}

/** Switches the board on (all pages side by side) or off. */
export function setBoardMode(store: EditorStore, on: boolean) {
  const { doc } = store.getState();
  if (!supportsBoard(doc) || Boolean(doc.board) === on) return;
  store.getState().update((draft) => {
    if (on) {
      draft.board = true;
      materializeBoard(draft);
    } else {
      delete draft.board;
    }
  });
  requestFit(store, on ? "all" : "page");
}

/** Lines the pages up again (rows of `columns`). */
export function arrangePages(store: EditorStore, columns?: number) {
  store.getState().update((draft) => arrangeBoard(draft, columns));
  requestFit(store, "all");
}

/** Moves a page on the board (one undo step per drag, see `merge`). */
export function movePageOnBoard(store: EditorStore, pageIndex: number, x: number, y: number) {
  store.getState().update((draft) => {
    const page = draft.pages[pageIndex];
    if (!page) return;
    page.x = Math.round(x);
    page.y = Math.round(y);
  });
}

export function duplicateCurrentPage(store: EditorStore, index = store.getState().pageIndex) {
  let created = -1;
  store.getState().update((draft) => {
    created = duplicatePage(draft, index);
  });
  if (created >= 0) store.getState().setPage(created);
}

export function removePageAt(store: EditorStore, index: number) {
  const { doc } = store.getState();
  if (doc.pages.length <= 1) return;
  store.getState().update((draft) => removePage(draft, index));
  store.getState().setPage(Math.max(0, index - 1));
}

export function movePageTo(store: EditorStore, from: number, to: number) {
  store.getState().update((draft) => movePage(draft, from, to));
  store.getState().setPage(to);
}

// ---------------------------------------------------------------------------
// Brand kit
// ---------------------------------------------------------------------------

/**
 * Applies a brand colour to the selection (text colour, shape fill, line or
 * image frame) or, without selection, to the page background. Linked to the kit.
 */
export function applyBrandColor(store: EditorStore, key: string) {
  const { selection, brand, pageIndex } = store.getState();
  if (!brand) return;
  store.getState().update((draft) => {
    if (selection.length === 0) {
      const page = draft.pages[pageIndex];
      const color = brand.colors.find((entry) => entry.key === key)?.color;
      if (page && color) {
        page.background = color;
        page.brand = { background: key };
      }
      return;
    }
    for (const id of selection) {
      const element = draft.elements[id];
      if (!element || element.locked) continue;
      if (element.type === "text") bindColor(element, "color", key, brand);
      else if (element.type === "shape") {
        if (element.shape === "line") bindColor(element, "stroke", key, brand);
        else bindColor(element, "fill", key, brand);
      } else if (element.type === "image" && element.stroke)
        bindColor(element, "stroke", key, brand);
      else if (element.type === "icon") bindColor(element, "color", key, brand);
    }
  });
}

/** Applies a brand text style to the selected texts, or adds a new text in that style. */
export function applyBrandTextStyle(store: EditorStore, key: string, label: string) {
  const { selection, doc, brand } = store.getState();
  if (!brand) return;
  const texts = selection.filter((id) => doc.elements[id]?.type === "text");
  if (texts.length === 0) {
    addBrandText(store, key, label);
    return;
  }
  const scale = brandScale(doc);
  store.getState().update((draft) => {
    for (const id of texts) {
      const element = draft.elements[id];
      if (element && !element.locked) bindTextStyle(element, key, brand, scale);
    }
  });
}
